import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  DoubleMaterialityIroTypeEnum,
  DoubleMaterialityStakeholderType,
  QuestionDto,
} from "@green0meter/api-client";
import { useForm, Controller } from "react-hook-form";
import { Autocomplete, TextField as MuiTextField } from "@mui/material";
import type { AssessmentDto } from "@green0meter/api-client";

import { IRO_VALUES, IRO_LABEL_MAP } from "../utils/IROUtils";
import { useESRSLabels } from "../../Stakeholders/components/useESRSLabels";

import { Drawer } from "@components/common";
import LabeledControllerField from "@components/common/LabeledControllerField/LabeledControllerField";
import {
  Stack,
  Typography,
  FormControl,
  Switch,
  FormControlLabel,
  Button,
  RadioGroup,
  Radio,
  Chip as MuiChip,
  Select,
  MenuItem,
} from "@components/base";
import RichTextBox from "@components/common/RichTextBox";
import { useUserStore } from "@store/user";
import {
  useCreateQuestion,
  useUpdateQuestion,
  useDeleteQuestion,
  useGetAssessments,
} from "@api";
import { getYearFromSearchParams } from "@helpers";

type QuestionEditorFormValues = {
  enabled: boolean;
  assessmentId: string;
  iro: DoubleMaterialityIroTypeEnum;
  stakeholderType: DoubleMaterialityStakeholderType;
  questions: Record<string, string>;
  descriptions: Record<string, string>;
  activateTextField: boolean;
  textFieldLabels: Record<string, string>;
};

interface QuestionEditorProps {
  open: boolean;
  onClose: () => void;
  question: QuestionDto | null;
}

const DMQuestionEditor: FC<QuestionEditorProps> = ({ open, onClose, question }) => {
  const { t, i18n } = useTranslation();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);
  const [isPendingOperation, setIsPendingOperation] = useState(false);
  const [activeLang, setActiveLang] = useState<string>("en");

  const { createQuestionAsync, isPendingCreateQuestion } = useCreateQuestion(user);
  const { updateQuestionAsync, isPendingUpdateQuestion } = useUpdateQuestion(user);
  const { deleteQuestionAsync, isPendingDeleteQuestion } = useDeleteQuestion(user);

  const isPendingAny =
    isPendingOperation ||
    isPendingCreateQuestion ||
    isPendingUpdateQuestion ||
    isPendingDeleteQuestion;

  const initialLanguageRef = useRef<string>(i18n.language?.split("-")[0] ?? "en");

  const form = useForm<QuestionEditorFormValues>({
    mode: "onChange",
    defaultValues: {
      enabled: true,
      assessmentId: "",
      iro: DoubleMaterialityIroTypeEnum.Impact,
      stakeholderType: DoubleMaterialityStakeholderType.External,
      questions: { [initialLanguageRef.current]: "" },
      descriptions: { [initialLanguageRef.current]: "" },
      activateTextField: false,
      textFieldLabels: { [initialLanguageRef.current]: "" },
    },
  });

  const { control, reset, handleSubmit, getValues } = form;

  const { assessments } = useGetAssessments(user, {
    year,
    pageSize: 1000,
    pageNumber: 1,
  });
  const assessmentOptions = assessments?.data ?? [];
  const { getEsrsLabelFromEnum } = useESRSLabels();

  const selectedAssessmentId = form.watch("assessmentId");
  const selectedAssessment = assessmentOptions.find(
    (a) => a.id === selectedAssessmentId,
  );

  useEffect(() => {
    if (question) {
      reset({
        enabled: question.enabled,
        assessmentId: question.assessmentId,
        iro: question.iro,
        stakeholderType: question.stakeholderType,
        questions: question.questions as Record<string, string>,
        descriptions: question.descriptions as Record<string, string>,
        activateTextField: question.activateTextField,
        textFieldLabels: (question.textFieldLabels as Record<string, string>) ?? {},
      });
      const firstLang = Object.keys(question.questions ?? {})[0];

      setActiveLang(firstLang || initialLanguageRef.current);
    } else {
      reset({
        enabled: true,
        assessmentId: "",
        iro: DoubleMaterialityIroTypeEnum.Impact,
        stakeholderType: DoubleMaterialityStakeholderType.External,
        questions: { [initialLanguageRef.current]: "" },
        descriptions: { [initialLanguageRef.current]: "" },
        activateTextField: false,
        textFieldLabels: { [initialLanguageRef.current]: "" },
      });
      setActiveLang(initialLanguageRef.current);
    }
  }, [question, reset]);

  const onSubmit = async (values: QuestionEditorFormValues): Promise<void> => {
    setIsPendingOperation(true);
    try {
      if (question) {
        await updateQuestionAsync(question.id, {
          enabled: values.enabled,
          assessmentId: values.assessmentId,
          iro: values.iro,
          stakeholderType: values.stakeholderType,
          questions: values.questions,
          descriptions: values.descriptions,
          activateTextField: values.activateTextField,
          textFieldLabels: values.textFieldLabels,
        });
      } else {
        await createQuestionAsync({
          enabled: values.enabled,
          assessmentId: values.assessmentId,
          iro: values.iro,
          stakeholderType: values.stakeholderType,
          questions: values.questions,
          descriptions: values.descriptions,
          activateTextField: values.activateTextField,
          textFieldLabels: values.textFieldLabels,
          year,
        });
      }
      onClose();
    } finally {
      setIsPendingOperation(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!question) return;
    setIsPendingOperation(true);
    try {
      await deleteQuestionAsync(question.id);
      onClose();
    } finally {
      setIsPendingOperation(false);
    }
  };

  const LANGUAGES: { code: string; label: string }[] = [
    { code: "cs", label: t("languages.cs", "Czech") },
    { code: "en", label: t("languages.en", "English") },
    { code: "sk", label: t("languages.sk", "Slovak") },
    { code: "bg", label: t("languages.bg", "Bulgarian") },
    { code: "de", label: t("languages.de", "German") },
    { code: "hu", label: t("languages.hu", "Hungarian") },
    { code: "pl", label: t("languages.pl", "Polish") },
  ];

  const content = (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Stack spacing={2} sx={{ flex: 1 }}>
        <FormControlLabel
          control={
            <Controller
              control={control}
              name="enabled"
              render={({ field }) => (
                <Switch {...field} checked={field.value} disabled={isPendingAny} />
              )}
            />
          }
          label={t("doubleMateriality.questions.fields.enabled", "Enabled")}
        />

        <LabeledControllerField
          label={t("assessments.fields.id", "Assessment ID")}
          name="assessmentId"
          control={control}
          disabled={isPendingAny}
          render={({ field, error }) => (
            <Autocomplete
              disablePortal
              options={assessmentOptions}
              value={assessmentOptions.find((o) => o.id === field.value) || null}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) =>
                `${opt.assessmentId} - ${opt.sustainabilityMatter}`
              }
              onChange={(
                _event: React.SyntheticEvent<Element, Event>,
                value: AssessmentDto | null,
              ) => field.onChange(value?.id || "")}
              renderInput={(params: Parameters<typeof MuiTextField>[0]) => (
                <MuiTextField
                  {...params}
                  error={Boolean(error)}
                  helperText={error?.message}
                  disabled={isPendingAny}
                  variant="filled"
                  size="small"
                  hiddenLabel
                  fullWidth
                />
              )}
            />
          )}
        />

        <LabeledControllerField
          label={t("stakeholders.table.stakeholderType", "Stakeholder type")}
          name="stakeholderType"
          control={control}
          disabled={isPendingAny}
          render={({ field, error }) => (
            <FormControl fullWidth error={Boolean(error)}>
              <RadioGroup
                row
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <FormControlLabel
                  value={DoubleMaterialityStakeholderType.Internal}
                  control={<Radio />}
                  label={t("stakeholders.type.internal", "Internal")}
                />
                <FormControlLabel
                  value={DoubleMaterialityStakeholderType.External}
                  control={<Radio />}
                  label={t("stakeholders.type.external", "External")}
                />
              </RadioGroup>
              {error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        <LabeledControllerField
          label={t("doubleMateriality.iro.title", "IRO")}
          name="iro"
          control={control}
          disabled={isPendingAny}
          render={({ field, error, disabled }) => (
            <FormControl fullWidth error={Boolean(error)}>
              <Select
                {...field}
                disabled={disabled}
                variant="filled"
                value={field.value ?? DoubleMaterialityIroTypeEnum.Impact}
              >
                {IRO_VALUES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(
                      value === DoubleMaterialityIroTypeEnum.Impact
                        ? "doubleMateriality.iro.impact"
                        : value === DoubleMaterialityIroTypeEnum.Risk
                          ? "doubleMateriality.iro.risk"
                          : "doubleMateriality.iro.opportunity",
                      IRO_LABEL_MAP[value],
                    )}
                  </MenuItem>
                ))}
              </Select>
              {error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {error.message}
                </Typography>
              )}
            </FormControl>
          )}
        />

        <Stack spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t("stakeholders.table.esrsArea", "Topic")}
          </Typography>
          <Typography variant="body1">
            {selectedAssessment
              ? getEsrsLabelFromEnum(selectedAssessment.topic)
              : "-"}
          </Typography>
        </Stack>

        <Typography variant="subtitle2">
          {t(
            "doubleMateriality.questions.languagesLabel",
            "Please create question and description in language that is relevant for you stakeholders",
          )}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {LANGUAGES.map((lng) => (
            <MuiChip
              key={lng.code}
              label={lng.label}
              color={activeLang === lng.code ? "primary" : "grey"}
              onClick={() => setActiveLang(lng.code)}
            />
          ))}
        </Stack>

        <LabeledControllerField
          label={t("doubleMateriality.questions.table.question", "Question")}
          name="questions"
          control={control}
          disabled={isPendingAny}
          render={({ field }) => {
            const value = field.value ?? {};
            const current = value[activeLang] ?? "";

            return (
              <RichTextBox
                key={`questions-${activeLang}`}
                placeholder={t(
                  "doubleMateriality.questions.table.questionPlaceholder",
                  "Type your question here...",
                )}
                value={current}
                onChange={(html) =>
                  field.onChange({
                    ...getValues("questions"),
                    [activeLang]: html,
                  })
                }
              />
            );
          }}
        />

        <LabeledControllerField
          label={t("doubleMateriality.questions.table.description", "Description")}
          name="descriptions"
          control={control}
          disabled={isPendingAny}
          render={({ field }) => {
            const value = field.value ?? {};
            const current = value[activeLang] ?? "";

            return (
              <RichTextBox
                key={`descriptions-${activeLang}`}
                placeholder={t(
                  "doubleMateriality.questions.table.descriptionPlaceholder",
                  "Type your question description here...",
                )}
                value={current}
                onChange={(html) =>
                  field.onChange({
                    ...getValues("descriptions"),
                    [activeLang]: html,
                  })
                }
              />
            );
          }}
        />

        <LabeledControllerField
          label={t(
            "doubleMateriality.questions.table.activateTextField",
            "Activate text field",
          )}
          name="activateTextField"
          control={control}
          disabled={isPendingAny}
          render={({ field }) => (
            <RadioGroup
              row
              value={field.value ? "yes" : "no"}
              onChange={(e) => field.onChange(e.target.value === "yes")}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={t("common.yes", "Yes")}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t("common.no", "No")}
              />
            </RadioGroup>
          )}
        />

        {form.watch("activateTextField") && (
          <LabeledControllerField
            label={t(
              "doubleMateriality.questions.table.textFieldLabel",
              "Open question",
            )}
            name="textFieldLabels"
            control={control}
            disabled={isPendingAny}
            render={({ field }) => {
              const value = field.value ?? {};
              const current = value[activeLang] ?? "";

              return (
                <RichTextBox
                  key={`textFieldLabels-${activeLang}`}
                  placeholder={t(
                    "doubleMateriality.questions.table.textFieldLabelPlaceholder",
                    "Type description here",
                  )}
                  value={current}
                  onChange={(html) =>
                    field.onChange({
                      ...getValues("textFieldLabels"),
                      [activeLang]: html,
                    })
                  }
                />
              );
            }}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {question && (
          <Button
            color="error"
            variant="outlined"
            onClick={handleDelete}
            disabled={isPendingAny}
          >
            {t("common.remove", "Delete")}
          </Button>
        )}
        <Button
          color="inherit"
          variant="outlined"
          onClick={onClose}
          disabled={isPendingAny}
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isPendingAny}
        >
          {question ? t("common.save", "Save") : t("common.add", "Add")}
        </Button>
      </Stack>
    </Stack>
  );

  const contentWithHeader = (
    <Stack spacing={3} sx={{ height: "100%" }}>
      <Typography variant="h6" color="text.primary">
        {question
          ? t("doubleMateriality.questions.editQuestion", "EDIT QUESTION")
          : t("doubleMateriality.questions.addQuestion", "ADD QUESTION")}
      </Typography>
      {content}
    </Stack>
  );

  return (
    <Drawer open={open} onClose={onClose} title="" content={contentWithHeader} />
  );
};

export default DMQuestionEditor;
